import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import clientService from 'src/clientService';
import { AcousticIndexEnum, ChartRequest, ChartResponse } from 'src/api/Api';

interface DeploymentChartProps {
  initialDeployments: string[];
  projectDeployments: any[];
  selectedLocation: any;
}

const DeploymentChart: React.FC<DeploymentChartProps> = ({
  initialDeployments,
  projectDeployments,
  selectedLocation,
}) => {
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [selectedDeployments, setSelectedDeployments] = useState<string[]>(initialDeployments);
  const [selectedIndex, setSelectedIndex] = useState<string>('aci');
  const [loading, setLoading] = useState(false);

  const fetchChartData = async (deployments: string[]) => {
    if (deployments.length === 0) {
      setChartData(null);
      return;
    }
    setLoading(true);
    try {
      const req: ChartRequest = {
        deploymentIds: deployments,
        index: [
          AcousticIndexEnum.Aci,
          AcousticIndexEnum.Bi,
          AcousticIndexEnum.Adi,
          AcousticIndexEnum.Aei,
          AcousticIndexEnum.Ndsi,
        ],
      };
      const res =
        await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      setChartData(res.data);
    } catch (err) {
      console.error('Error fetching deployment chart:', err);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  // fetch initially with analytics deployment
  useEffect(() => {
    fetchChartData(initialDeployments);
  }, [initialDeployments]);

  // fetch whenever user changes dropdown
  useEffect(() => {
    fetchChartData(selectedDeployments);
  }, [selectedDeployments]);

  const options = useMemo(() => {
    if (!chartData) return null;

    const deploymentsToShow = chartData.comparison.filter((c) =>
      selectedDeployments.includes(c.deployment_id),
    );

    const series = deploymentsToShow.map((c) => {
      const deployment = projectDeployments.find((d) => d.deployment_id === c.deployment_id);
      const deploymentName = deployment?.deployment_name || c.deployment_id;

      return {
        type: 'line',
        name: `${deploymentName} (${selectedIndex.toUpperCase()})`,
        data: (c.indices[selectedIndex] || []).map((v: number | null | undefined) => v ?? 0),
      };
    });

    return {
      chart: { type: 'line', zoomType: 'x' },
      title: { text: 'Deployment-wise Activity' },
      xAxis: {
        title: { text: 'Time of Day' },
        categories: chartData.time_slots,
        labels: { rotation: -45 },
      },
      yAxis: {
        title: { text: 'Average of Acoustic Indices' },
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return (this.value as number).toFixed(2);
          },
        },
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point) {
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${(
            this.y as number
          ).toFixed(2)}</b><br/>`;
        },
      },
      series,
    };
  }, [chartData, selectedDeployments, selectedIndex]);

  return (
    <Box sx={{ height: '500px', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Select
          multiple
          variant="standard"
          disableUnderline
          value={selectedDeployments}
          onChange={(e) => setSelectedDeployments(e.target.value as string[])}
          renderValue={() => 'Deployments'}
          sx={{
            fontSize: '.8rem',
            textAlign: 'center',
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 0,
            p: 0,
            m: 0,
            '& fieldset': { border: 'none' },
            '&:hover': {
              borderColor: 'primary.dark',
            },
            '&.Mui-focused': {
              borderColor: 'primary.main',
              borderBottomWidth: '2px',
            },
            '& .MuiSelect-select': {
              py: 0.5,
            },
          }}
        >
          {projectDeployments
            .filter((d) => d.location_id === selectedLocation?.location_id)
            .map((d) => (
              <MenuItem key={d.deployment_id} value={d.deployment_id}>
                <Checkbox checked={selectedDeployments.includes(d.deployment_id)} />
                <ListItemText primary={d.deployment_name} />
              </MenuItem>
            ))}
        </Select>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Select
            variant="standard"
            disableUnderline
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(e.target.value)}
            sx={{
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 0,
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                borderColor: 'primary.dark',
              },
              '&.Mui-focused': {
                borderColor: 'primary.main',
                borderBottomWidth: '2px',
              },
              '& .MuiSelect-select': {
                py: 0.5,
              },
              textAlign: 'center',
            }}
          >
            <MenuItem value="aci">ACI</MenuItem>
            <MenuItem value="bi">BI</MenuItem>
            <MenuItem value="adi">ADI</MenuItem>
            <MenuItem value="aei">AEI</MenuItem>
            <MenuItem value="ndsi">NDSI</MenuItem>
          </Select>
          <Tooltip
            title={
              <Box>
                <Typography variant="body2">
                  <b>ACI</b> – Acoustic Complexity Index
                </Typography>
                <Typography variant="body2">
                  <b>BI</b> – Bioacoustic Index
                </Typography>
                <Typography variant="body2">
                  <b>ADI</b> – Acoustic Diversity Index
                </Typography>
                <Typography variant="body2">
                  <b>AEI</b> – Acoustic Evenness Index
                </Typography>
                <Typography variant="body2">
                  <b>NDSI</b> – Normalized Difference Soundscape Index
                </Typography>
              </Box>
            }
            placement="right"
            arrow
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {loading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85%' }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        chartData && <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Box>
  );
};

export default DeploymentChart;
